import React, { useEffect, useState, useCallback, useContext } from "react";

/** Context so DialogClose (and Sheet shim) can close the dialog */
const DialogCtx = React.createContext({ close: () => {} });

export function Dialog({ open, onOpenChange, children }) {
  const [internalOpen, setInternalOpen] = useState(Boolean(open));
  const isControlled = typeof open === "boolean";
  const isOpen = isControlled ? open : internalOpen;

  useEffect(() => {
    if (isControlled) setInternalOpen(open);
  }, [isControlled, open]);

  const close = useCallback(() => {
    if (onOpenChange) onOpenChange(false);
    else setInternalOpen(false);
  }, [onOpenChange]);

  // Close on ESC + lock body scroll
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  return (
    <DialogCtx.Provider value={{ close }}>
      <div
        className="dialog-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        {children}
      </div>
    </DialogCtx.Provider>
  );
}

export function DialogTrigger({ children, onClick }) {
  return (
    <span onClick={onClick} style={{ display: "inline-block" }}>
      {children}
    </span>
  );
}

/**
 * The visible panel. Default centers like a modal.
 * The Sheet shim overrides these styles to anchor right/bottom.
 */
export function DialogContent({ children, style, className = "", ...rest }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
      className={`dialog-content ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
}

export function DialogHeader({ children, style, className = "" }) {
  return (
    <div className={`dialog-header ${className}`} style={style}>
      {children}
    </div>
  );
}

export function DialogTitle({ children, style, className = "" }) {
  return (
    <h2 className={`dialog-title ${className}`} style={style}>
      {children}
    </h2>
  );
}

export function DialogDescription({ children, style, className = "" }) {
  return (
    <p className={`dialog-description ${className}`} style={style}>
      {children}
    </p>
  );
}

export function DialogFooter({ children, style, className = "" }) {
  return (
    <div className={`dialog-footer ${className}`} style={style}>
      {children}
    </div>
  );
}

/** Close button that works anywhere inside the dialog */
export function DialogClose({ asChild, children, onClick, ...rest }) {
  const { close } = useContext(DialogCtx);

  if (asChild && React.isValidElement(children)) {
    const child = React.Children.only(children);
    return React.cloneElement(child, {
      ...rest,
      onClick: (e) => {
        child.props?.onClick?.(e);
        onClick?.(e);
        close();
      },
    });
  }

  return (
    <button
      type="button"
      {...rest}
      onClick={(e) => {
        onClick?.(e);
        close();
      }}
    >
      {children ?? "Close"}
    </button>
  );
}
